import { useCallback, useEffect, useState } from "react";
import type { PeriodLockDto, PeriodLockInput } from "@delta/shared";
import { useLang } from "../../i18n";
import { api } from "../../lib/api";
import { errorText, fieldErrorsOf } from "../../lib/errors";
import { Icon } from "../../components/Icon";
import { PageHead, Shell } from "../../components/Shell";
import { useToast } from "../../components/Toast";

// Settings › Period lock (decision P-14; rules K-1 to K-4, provisional). Nothing is posted on or before the lock date:
// the server refuses it for every document. Lowering the date reopens a locked period and needs a reason; every change
// is listed below from the audit log. Not in the legacy system, so the screen follows the Organization screen's layout.

export function PeriodLockSettings() {
  const { t, lang } = useLang();
  const toast = useToast();
  const [x, setX] = useState<PeriodLockDto | null>(null);
  const [date, setDate] = useState("");
  const [reason, setReason] = useState("");
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [failed, setFailed] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setFailed(null);
    try {
      const d = await api<PeriodLockDto>("GET", "/settings/period-lock");
      setX(d); setDate(d.lockedUntil ?? ""); setReason(""); setErrs({});
    } catch (e) { setFailed(errorText(t, e)); }
  }, [t]);
  useEffect(() => { void load(); }, [load]);

  const next = date || null;
  const dirty = !!x && next !== x.lockedUntil;
  const reopening = !!x && x.lockedUntil !== null && (next === null || next < x.lockedUntil);
  const when = (iso: string) => new Date(iso).toLocaleString(lang === "ar" ? "ar-SA-u-nu-latn-ca-gregory" : "en-GB", { dateStyle: "medium", timeStyle: "short", timeZone: "Asia/Riyadh" });

  async function save() {
    const e: Record<string, string> = {};
    if (x && next && next > x.today) e.lockedUntil = t("lockFuture");
    if (reopening && !reason.trim()) e.reason = t("requiredField");
    setErrs(e);
    if (Object.keys(e).length) return;
    setBusy(true);
    try {
      const body: PeriodLockInput = { lockedUntil: next, reason: reason.trim() };
      const d = await api<PeriodLockDto>("PUT", "/settings/period-lock", body);
      setX(d); setDate(d.lockedUntil ?? ""); setReason("");
      toast(d.lockedUntil ? t("lockSaved", { x: d.lockedUntil }) : t("lockCleared"));
    } catch (err) {
      setErrs(fieldErrorsOf(t, err));
      toast(errorText(t, err));
    } finally { setBusy(false); }
  }

  const body = failed ? (
    <section className="card"><div className="state-box error"><Icon name="alert" /><h3>{t("loadErrorTitle")}</h3><p>{failed}</p><button className="btn btn-primary" onClick={() => void load()}><Icon name="refresh" />{t("retry")}</button></div></section>
  ) : !x ? <section className="card"><div className="state-box"><p>{t("loading")}</p></div></section> : (
    <div className="set-stack">
      <section className="set-sec">
        <div className="set-sec-h"><h2>{t("lockDate")}</h2><p>{t("lockDateHint")}</p></div>
        <div className="card set-sec-b"><div className="form-grid">
          <div className="field full" data-testid="lock-state">
            <span className="label">{t("status")}</span>
            {x.lockedUntil
              ? <span><span className="pill warn"><Icon name="lock" />{t("lockedUntil", { x: x.lockedUntil })}</span>{x.lockedBy && x.lockedAt && <span className="hint"> {t("lockSetBy", { u: x.lockedBy.name, at: when(x.lockedAt) })}</span>}</span>
              : <span className="pill good">{t("noLock")}</span>}
          </div>
          <div className={`field ${errs.lockedUntil ? "invalid" : ""}`}>
            <label htmlFor="pl-date">{t("lockDate")}</label>
            <input id="pl-date" className="control num" type="date" dir="ltr" max={x.today} value={date} onChange={(e) => { setDate(e.target.value); setErrs((o) => ({ ...o, lockedUntil: "" })); }} />
            <div className="ferr" role="alert">{errs.lockedUntil}</div><span className="hint">{t("lockMaxToday", { x: x.today })}</span>
          </div>
          {reopening && (
            <div className={`field span-2 ${errs.reason ? "invalid" : ""}`}>
              <label htmlFor="pl-reason" className="req">{t("lockReason")}</label>
              <input id="pl-reason" className="control" style={{ textAlign: "start" }} value={reason} maxLength={500} onChange={(e) => { setReason(e.target.value); setErrs((o) => ({ ...o, reason: "" })); }} />
              <div className="ferr" role="alert">{errs.reason}</div><span className="hint">{t("lockReasonHint")}</span>
            </div>
          )}
          <div className="dep-note full" data-testid="lock-note"><Icon name="info" /><span>{t("lockProvisional")}</span></div>
        </div></div>
      </section>
      <section className="set-sec">
        <div className="set-sec-h"><h2>{t("lockHistory")}</h2></div>
        <div className="card set-sec-b">
          {x.history.length === 0 ? <div className="state-box"><p>{t("lockNoHistory")}</p></div> : (
            <table className="tbl" data-testid="lock-history">
              <thead><tr><th>{t("date")}</th><th>{t("user")}</th><th>{t("lockFrom")}</th><th>{t("lockTo")}</th><th>{t("lockReason")}</th></tr></thead>
              <tbody>{x.history.map((h, i) => (
                <tr key={i}><td className="num">{when(h.at)}</td><td>{h.by}</td><td className="num">{h.before ?? "–"}</td><td className="num">{h.after ?? "–"}</td><td>{h.reason || "–"}</td></tr>
              ))}</tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  );

  const bar = x && (
    <div className="action-bar">
      <div className="ab-stats"><div><div className="k">{t("status")}</div><div className="v" data-testid="form-state">{dirty ? <span className="pill warn">{t("unsavedChanges")}</span> : <span className="pill good">{t("allSaved")}</span>}</div></div></div>
      <div className="ab-actions">
        <button className="btn" disabled={!dirty || busy} onClick={() => { setDate(x.lockedUntil ?? ""); setReason(""); setErrs({}); }}>{t("discardChanges")}</button>
        <button className="btn btn-primary" data-testid="lock-save" disabled={!dirty || busy} onClick={() => void save()}><Icon name="check" />{busy ? t("saving") : t("save")}</button>
      </div>
    </div>
  );

  return (
    <Shell navKey="/settings/period-lock" bar={bar}>
      <PageHead crumbs={[[t("home"), "/home"], [t("settings")], [t("setPeriodLock")]]} title={t("setPeriodLock")} />
      {body}
    </Shell>
  );
}
